/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const SpawnerCtor = (typeof globalThis !== "undefined" && (globalThis.SpawnerNode || globalThis.Spawner)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const SPAWNER_PROPS = ["src", "applyGravity"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class SpawnerEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    SPAWNER_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              if (prop === "applyGravity") {
                sourceObject[prop] = !!v;
              } else {
                sourceObject[prop] = v;
              }
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter (PRESERVED ECS LOGIC)                                      */
/* ────────────────────────────────────────────────────────────────────────── */
class SpawnerAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const CMP = "spawner";
    const pending = (this._pending = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      const cur = { ...(el.getAttribute(CMP) || {}) };
      
      // Handle special case for applyGravity -> mediaOptions.applyGravity
      Object.keys(pending).forEach(k => {
        if (k === "applyGravity") {
          const mediaOptions = { ...(cur.mediaOptions || {}) };
          mediaOptions.applyGravity = !!pending[k];
          cur.mediaOptions = mediaOptions;
        } else {
          cur[k] = pending[k];
        }
        delete pending[k];
      });
      
      el.setAttribute(CMP, cur);
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === CMP) flush();
    });

    const setComponentProp = (key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(CMP);
      if (!attr) {
        pending[key] = val;
        return;
      }
      
      const current = { ...attr };
      if (key === "applyGravity") {
        const mediaOptions = { ...(current.mediaOptions || {}) };
        mediaOptions.applyGravity = !!val;
        current.mediaOptions = mediaOptions;
      } else {
        current[key] = val;
      }
      el.setAttribute(CMP, current);
    };

    const getComponentProp = key => {
      const el = this.sourceObject?.el;
      if (!el) return pending[key];
      const attr = el.getAttribute(CMP);
      if (key === "applyGravity") {
        return (attr?.mediaOptions?.applyGravity ?? pending[key]) ?? false;
      }
      return (attr && attr[key]) ?? pending[key];
    };

    Object.defineProperty(this, "src", {
      get: () => getComponentProp("src"),
      set: v => setComponentProp("src", v)
    });

    Object.defineProperty(this, "applyGravity", {
      get: () => getComponentProp("applyGravity"),
      set: v => setComponentProp("applyGravity", v)
    });
  }

  dispose() {
    this.sourceObject = null;
    this._pending = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of SPAWNER_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new SpawnerAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new SpawnerEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class SpawnerElement extends ScriptableObject {
  static scriptableType = "spawner";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("spawner", {
        src: initial.src || "",
        mediaOptions: { applyGravity: initial.applyGravity || false }
      });

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      
      // CRITICAL ECS INITIALIZATION PRESERVED
      if (typeof ScriptableObject.spawnerEcsInit === "function") {
        try {
          ScriptableObject.spawnerEcsInit(el, initial);
        } catch (e) {
          console.warn("SpawnerElement: Failed to initialise ECS entity", e);
        }
      }

      // Apply initial properties - Apply all properties including src to ensure proper synchronization
      for (const k of Object.keys(initial)) {
        if (k in obj) {
          obj[k] = initial[k];
        }
      }
      
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createSpawnerElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public spawner props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of SPAWNER_PROPS) {
      take(p, source[p]);
    }

    // 3) Don't clear existing children on A-Frame context - let the new SpawnerElement keep its ECS-created entities
    // This ensures functionality (networking, physics, interaction) is preserved
    //if (!HAS_AFRAME) {
      // Clear existing children for editor mode
      for (const c of [...this.children]) {
        this.remove(c);
      }
    //}

    // 4) Copy children from source
    // WARNING: This will create duplicate infrastructure children (guides, ECS entities)
    // Each SpawnerElement needs its own ECS infrastructure to work properly (networking, physics, interaction)
    // The duplication is intentional because we don't have a reliable way to identify the children that are ECS-created - each clone gets both:
    //   - Its own functional ECS infrastructure (created during construction)
    //   - Copies of all source children (including source's infrastructure)
    // This ensures the clone works properly even though it has some duplicate infrastructure objects.
    if (recursive) {
      const sourceChildren = logicalChildrenOf(source);
      
      for (const srcChild of sourceChildren) {
        const clonedChild = srcChild.clone(true);
        this.add(clonedChild);
      }
      
      // Ensure matrix world is updated after adding children
      this.updateMatrixWorld(true);
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of SPAWNER_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }


    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : SpawnerElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new SpawnerElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor SpawnerNode instance to Scriptable, attach adapter and proxies
function makeEditorSpawnerNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || SpawnerCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: SpawnerElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = SpawnerElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new SpawnerEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new SpawnerEditorAdapter(node, descriptors);
  }

  for (const prop of SPAWNER_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
SPAWNER_PROPS.forEach(prop => {
  Object.defineProperty(SpawnerElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createSpawnerElementEditor(initial = {}) {
  const SpawnerCtor = globalThis.SpawnerNode;

  if (typeof SpawnerCtor !== "function") {
    throw new Error("SpawnerElementEditor: SpawnerNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new SpawnerCtor(editor);
  } catch (e) {
    throw new Error(`SpawnerElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorSpawnerNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("spawner", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== SpawnerElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return SpawnerElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorSpawnerNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(SpawnerElement.prototype, Symbol.toStringTag, { value: "SpawnerElement" });

// Legacy exports for backward compatibility
export { SpawnerAFrameAdapter, SpawnerEditorAdapter, SpawnerElement as SpawnerScriptableObject };